import { WppTypography, WppInput } from '@wppopen/components-library-react'
import { CircleFlag } from 'react-circle-flags'
import { GenericModal } from 'components/Modals'
import { useState } from 'react'

interface GenerateContentModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: () => Promise<void>
  selectedRegion: any
  isLoading: boolean
}

export const GenerateContentModal = ({
  isOpen,
  onClose,
  onSubmit,
  selectedRegion,
  isLoading,
}: GenerateContentModalProps) => {
  // state to keep track of whether the user has attempted to generate content
  const [hasAttemptedGenerate, setHasAttemptedGenerate] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const formatErrorMessage = (message: string) => {
    // Check if it's the "0 products" case
    if (message.includes('0 products')) {
      return (
        <>
          You have <b>0 products</b> uploaded. <br />
          To generate content you need to have at least one product uploaded and ensure the product data quality is
          good.
        </>
      )
    }

    // For other cases, set the products count in bold
    const match = message.match(/(\d+\s+products?)/)
    if (!match) return message

    const [fullMatch] = match
    const parts = message.split(fullMatch)

    return (
      <>
        {parts[0]}
        <b>{fullMatch}</b>
        {parts[1]} <br />
        To generate content you need to ensure all product data quality is good.
      </>
    )
  }

  const getModalMessage = () => {
    if (errorMessage) {
      return formatErrorMessage(errorMessage)
    }

    return 'Are you sure you want to generate content? You will not be able to edit the region or data once this starts.'
  }

  const handleClose = () => {
    onClose()
    setHasAttemptedGenerate(false)
    setErrorMessage(null)
  }

  const handleSubmit = async () => {
    if (errorMessage) {
      handleClose()
      return
    }

    if (!hasAttemptedGenerate) {
      setHasAttemptedGenerate(true)
    }

    try {
      await onSubmit()
    } catch (error: any) {
      if (error?.status === 422) {
        setErrorMessage(error.errors[0].message)
        return
      }
    }
  }

  // Format the region display value
  const getRegionDisplayValue = () => {
    const regionName = selectedRegion?.region_name || 'Unknown Region'
    const locale = selectedRegion?.region_locale?.toUpperCase().replace('_', '-') || ''
    return `${regionName} (${locale})`
  }

  return (
    <GenericModal
      onCancel={!errorMessage ? handleClose : undefined}
      onSubmit={handleSubmit}
      modalTitle={!errorMessage ? 'Generate content' : 'Unable to generate content'}
      submitButtonText={!errorMessage ? 'Generate' : 'Ok'}
      shouldModalOpen={isOpen}
      isApiLoading={isLoading}
    >
      <WppInput
        labelConfig={{ text: 'Region' }}
        data-testid="region-input"
        required
        readOnly
        value={getRegionDisplayValue()}
      >
        <CircleFlag slot="icon-start" countryCode={selectedRegion?.region_flag} height="20" />
      </WppInput>
      <WppTypography tag="p" type="s-body" data-testid="modal-message">
        {getModalMessage()}
      </WppTypography>
    </GenericModal>
  )
}
